import { createSelector } from '@reduxjs/toolkit';
import {
    get,
    isEmpty,
    isNil,
    isObjectLike,
    some,
    last,
    find,
    sortBy,
    castArray,
    pipe,
    map,
    flatten,
    uniq,
    concat
} from 'lodash/fp';
import { ShareType } from 'app/lib/types/server';
import { toStringFqn } from 'app/lib/utils';
import { createDeepEqualitySelector, getTranslatedError } from 'app/store/utils';
import { Graph } from 'app/lib/utils/graph';
import { GROUP_NODE, RequestStatus, ErrorLevel } from 'app/lib/constants';
import {
    GENERIC_TRANSFORM_TYPE,
    DRAFT_MODEL_DTO_TYPE,
    MODEL_DTO_TYPE
} from 'app/store/__generated__/types';
import {
    MODEL_NOT_CONNECTED,
    HAS_DRAFT_MODEL_UPSTREAM,
    TRANSFORMATION_OUT_OF_SYNC
} from 'app/store/__generated__/errors';
import {
    selectCurrentProjectId,
    selectErrors,
    selectWarnings,
    selectProject,
    selectSelectedEntityId,
    setDeployedFrom,
    setErrors,
    selectRawTransformationsInCurrentProject
} from 'app/store/projects/selectors.js';
import {
    selectSharesForCurrentProject,
    selectShareErrorsForCurrentProject
} from 'app/store/shares/selectors.js';
import { selectEntitiesById } from 'app/store/entities/selectors.js';
import { selectInDeploymentDatasources } from 'app/store/deployView/selectors.js';
import {
    selectResolvedValidationsForSourceId,
    selectValidationStatusesBySourceFqn,
    selectValidationsValuesBySourceFqn,
    selectSourceValidationErrors
} from 'app/store/access/selectors';
import { selectDatasetQueries } from 'app/store/snowflakeQueries/selectors.js';
import { selectModelJobLastRunStatuses } from 'app/store/projectModels/selectors.js';
import { decorateError, getViewErrors, getViewWarnings } from 'app/store/view/utils.js';
import { resolveFlowTypeForViewEntity } from './utils';

const selectSlice = get('flow');
const selectPropEntityId = (_, props) => props.entityId;

const EMPTY_ARRAY = [];

export const selectDefaultFlowModeForCurrentProject = createSelector(
    selectSlice,
    selectCurrentProjectId,
    (slice, projectId) => slice.defaultFlowModeByProjectId[projectId]
);

export const selectSelectedNodesForCurrentProject = createSelector(
    selectSlice,
    selectCurrentProjectId,
    (slice, projectId) => slice.selectedNodesByProjectId[projectId] ?? EMPTY_ARRAY
);

const selectGroupExpandedStateById = createSelector(selectSlice, get('groupExpandedStateById'));

export const selectIsExpandedForPropEntityId = createSelector(
    selectGroupExpandedStateById,
    selectPropEntityId,
    (isExpandedByEntityId, entityId) => isExpandedByEntityId[entityId] ?? false
);

export const selectGroups = createSelector(
    selectProject,
    selectEntitiesById,
    selectGroupExpandedStateById,
    (project, entitiesById, isExpandedByEntityId) => {
        if (isNil(project)) {
            return EMPTY_ARRAY;
        }

        const groupIds = project.groupIds ?? EMPTY_ARRAY;

        return groupIds.map((groupId) => {
            const entityId = `${project.uid}.${groupId}`;
            const group = entitiesById[entityId];
            const isExpanded = isExpandedByEntityId[entityId] ?? false;
            return { ...group, id: groupId, isExpanded };
        });
    }
);

export const selectHasCollapsedGroups = createSelector(selectGroups, (groups) =>
    groups.some((group) => !group.isExpanded)
);

export const selectHasExpandedGroups = createSelector(selectGroups, (groups) =>
    groups.some((group) => group.isExpanded)
);

const selectGraphNodes = createSelector(
    selectProject,
    selectSharesForCurrentProject,
    (project, shares) => {
        if (isNil(project)) {
            return EMPTY_ARRAY;
        }

        const { viewUids } = project;
        const shareUids = shares?.map((s) => s.uid) ?? EMPTY_ARRAY;

        return [...viewUids, ...shareUids];
    }
);

export const selectGraphEdges = createSelector(
    selectProject,
    selectSharesForCurrentProject,
    (project, shares) => {
        if (isNil(project)) {
            return EMPTY_ARRAY;
        }

        const { dependencies } = project;
        const nodeEdges = dependencies.map((d) => ({ from: d.toView, to: d.fromView }));
        const shareEdges = shares?.map((s) => ({ from: s.parentUid, to: s.uid })) ?? EMPTY_ARRAY;

        return [...nodeEdges, ...shareEdges];
    }
);

export const selectDependencyGraph = createDeepEqualitySelector(
    selectGraphNodes,
    selectGraphEdges,
    (nodes, edges) => new Graph([...nodes], [...edges])
);

export const selectGroupedDependencyGraph = createDeepEqualitySelector(
    selectDependencyGraph,
    selectGroups,
    (dependencyGraph, groups = EMPTY_ARRAY) => {
        let graph = dependencyGraph;

        groups.forEach((group) => {
            if (!group.isExpanded) {
                graph = graph.group(group.id, group.children);
            }
        });

        return graph;
    }
);

export const selectionContainsGroupNodes = createSelector(
    selectSelectedNodesForCurrentProject,
    selectEntitiesById,
    selectCurrentProjectId,
    (selection, entitiesById, projectId) =>
        selection.some((id) => entitiesById[`${projectId}.${id}`]?._type === GROUP_NODE)
);

export const selectionCreatesCycles = createSelector(
    selectSelectedNodesForCurrentProject,
    selectDependencyGraph,
    (selection, dependencyGraph) => {
        const grouped = dependencyGraph.group('SELECTION', selection);

        return !grouped.isAcyclic();
    }
);

export const selectionCreatesCollapsedCycles = createSelector(
    selectSelectedNodesForCurrentProject,
    selectDependencyGraph,
    selectGroups,
    (selection, dependencyGraph, groups) => {
        let graph = dependencyGraph;

        groups.forEach((group) => {
            graph = graph.group(group.id, group.children);
        });

        graph = graph.group('SELECTION', selection);

        return !graph.isAcyclic();
    }
);

export const selectionContainsChildNodes = createSelector(
    selectSelectedNodesForCurrentProject,
    selectGroups,
    (selection, groups) => {
        const allChildren = [...new Set(groups.map((group) => group.children).flat())];

        return selection.some((node) => allChildren.includes(node));
    }
);

export const selectCanCreateGroup = createSelector(
    selectSelectedNodesForCurrentProject,
    selectionContainsGroupNodes,
    selectionCreatesCycles,
    selectionCreatesCollapsedCycles,
    selectionContainsChildNodes,
    (selection, containsGroupNodes, createsCycles, createsCollapsedCycles, containsChildNodes) =>
        !isEmpty(selection) &&
        !containsGroupNodes &&
        !createsCycles &&
        !createsCollapsedCycles &&
        !containsChildNodes
);

function createNodeFromView(view) {
    const count = view.operationUids?.length ?? null;
    const displayType = resolveFlowTypeForViewEntity(view);
    const model = view.payload?.model;

    return {
        type: view._type === GENERIC_TRANSFORM_TYPE ? view.payload.type : view._type,
        id: view.uid,
        uid: view.uid,
        nodeId: view.id,
        spaceUid: view.spaceUid,
        entityId: view.entityId,
        slug: view.slug,
        name: view.name,
        modifiedAt: view.modifiedAt,
        source: view.source,
        sourceType: view.sourceType,
        error: view.error,
        deployedAs: view.deployedAs,
        deployedFrom: view.deployedFrom,
        previewInformation: view.previewInformation,
        level: view.level,
        trigger: view.trigger,
        count,
        model,
        displayType,
        hasProjection: view.hasProjection,
        hasModel: view.hasModel,
        jobDefUid: view.jobDefUid,
        isOutOfSync: view.isOutOfSync
    };
}

function createGroupNode(group) {
    const { id, entityId, name, slug, children } = group;
    return {
        type: GROUP_NODE,
        id,
        uid: id,
        entityId,
        name,
        slug,
        count: children.length,
        displayType: GROUP_NODE
    };
}

function createNodeFromShare(share) {
    const { _type, uid, ...rest } = share;
    return {
        ...rest,
        type: _type,
        id: uid,
        uid,
        isOutput: true,
        displayType: _type
    };
}

const selectNodeIdProp = (_, props) => props.nodeId;

const selectEntityForPropNodeId = createSelector(
    selectCurrentProjectId,
    selectNodeIdProp,
    selectEntitiesById,
    (projectId, nodeId, entities) => entities[`${projectId}.${nodeId}`]
);

const selectNodeSourceFqnObject = createSelector(selectEntityForPropNodeId, get('tableFqn'));

const selectNodeSourceFqnString = createSelector(selectNodeSourceFqnObject, toStringFqn);

const selectIsInProgress = createSelector(
    selectNodeSourceFqnObject,
    selectInDeploymentDatasources,
    (fqnObject, inProgressEntities) => fqnObject && !isNil(find(fqnObject, inProgressEntities))
);

const selectIsValidatingSource = createSelector(
    selectNodeSourceFqnString,
    selectValidationStatusesBySourceFqn,
    (fqn, validationStatuses) => fqn && validationStatuses[fqn] === RequestStatus.PENDING
);

const selectIsUpstreamValidationRunning = createSelector(
    selectEntityForPropNodeId,
    selectResolvedValidationsForSourceId,
    (entity, validations) =>
        entity && some((validation) => validation?.running, validations[entity.entityId])
);

export const selectIsNodeSelectedForIdProp = createSelector(
    selectNodeIdProp,
    selectSelectedNodesForCurrentProject,
    (nodeId, selection) => selection?.includes(nodeId)
);

export const selectCurrentNodeId = createSelector(selectSelectedEntityId, (currentNodeId) =>
    last(currentNodeId?.split('.'))
);

function applyValidationsToView(view, validations) {
    if (!view.tableFqn) {
        return { ...view };
    }

    const fqn = toStringFqn(view.tableFqn);

    if (fqn && fqn in validations) {
        const { rowCount, sampleSize, tableType: sourceType } = validations[fqn];
        return {
            ...view,
            sourceType,
            previewInformation: { recordCount: rowCount, sampleSize }
        };
    }

    return { ...view };
}

function enrichNode(node) {
    return {
        ...node,
        isDeployed: isObjectLike(node.deployedFrom),
        isSampled: !isNil(node?.previewInformation?.sampleSize)
    };
}

export const selectFlowNodesById = createSelector(
    [
        selectEntitiesById,
        selectSharesForCurrentProject,
        selectGroups,
        selectErrors,
        selectValidationsValuesBySourceFqn,
        selectWarnings
    ],
    (entitiesById, shares, groups, errors, validations, warnings) => {
        const nodesById = {};

        Object.values(entitiesById).forEach((entity) => {
            if (isNil(entity)) {
                return;
            }

            let view = applyValidationsToView(entity, validations);
            view = setErrors(view, errors);
            view = setDeployedFrom(view, entitiesById);

            if (view._type === MODEL_DTO_TYPE) {
                const isOutOfSync = warnings[entity.uid]?.some(
                    (warning) => warning._type === TRANSFORMATION_OUT_OF_SYNC
                );

                view.isOutOfSync = Boolean(isOutOfSync);
            }
            const node = createNodeFromView(view);

            nodesById[node.uid] = enrichNode(node);
        });

        shares?.forEach((share) => {
            const node = createNodeFromShare(share);
            nodesById[node.uid] = enrichNode(node);
        });

        groups.forEach((group) => {
            const node = createGroupNode(group);
            nodesById[node.uid] = enrichNode(node);
        });

        return nodesById;
    }
);

export const selectFlowNodeForIdProp = createSelector(
    [selectNodeIdProp, selectFlowNodesById],
    (nodeId, nodesById) => nodesById[nodeId] ?? null
);

export const selectDraftNodeIds = createSelector(
    [selectFlowNodesById, selectDependencyGraph],
    (nodesById, dependencyGraph) => {
        const nodesWithUpstream = new Set(dependencyGraph.edges.map((e) => e.to));

        return Object.values(nodesById)
            .filter(
                (node) => node.type === DRAFT_MODEL_DTO_TYPE && !nodesWithUpstream.has(node.uid)
            )
            .map((node) => node.uid);
    }
);

export const selectFlowNodeIsProcessingForIdProp = createSelector(
    selectIsInProgress,
    selectIsValidatingSource,
    selectIsUpstreamValidationRunning,
    (isInProgress, isValidatingSource, isUpstreamValidationRunning) =>
        isInProgress || isValidatingSource || isUpstreamValidationRunning
);

const selectAllProjectNodesWithProblems = createSelector(
    selectRawTransformationsInCurrentProject,
    selectWarnings,
    selectErrors,
    selectSourceValidationErrors,
    selectModelJobLastRunStatuses,
    selectShareErrorsForCurrentProject,
    selectDatasetQueries,
    (
        nodes,
        warnings,
        errors,
        sourceValidationErrors,
        jobLastRunStatuses,
        shareErrors,
        datasets
    ) => {
        const errorNodes = {};
        nodes.forEach((node) => {
            const { entityId, name, uid, tableFqn, spaceSlug } = node;
            errorNodes[entityId] = {
                name: name,
                uid: uid,
                entityId: entityId,
                fqn: toStringFqn(tableFqn),
                spaceSlug,
                errors: []
            };

            const viewError = getViewErrors(decorateError(errorNodes, node, errors));
            const viewWarnings = getViewWarnings(warnings[uid]);
            const datasetError = datasets[entityId]?.error;

            errorNodes[entityId].errors = [...viewError, ...viewWarnings];
            if (!isEmpty(datasetError)) {
                errorNodes[entityId].errors.push(getTranslatedError(datasetError));
            }
            if (tableFqn) {
                const status = sourceValidationErrors[toStringFqn(tableFqn)];
                if (!isEmpty(status)) {
                    errorNodes[entityId].errors = [...errorNodes[entityId].errors, ...status];
                }
            }

            if (jobLastRunStatuses[entityId]) {
                errorNodes[entityId].errors = [
                    ...errorNodes[entityId].errors,
                    ...jobLastRunStatuses[entityId]
                ];
            }

            if (isEmpty(errorNodes[entityId].errors)) {
                delete errorNodes[entityId];
            } else {
                errorNodes[entityId].errorLevel = errorNodes[entityId].errors.some(
                    (error) => error.level === ErrorLevel.ERROR
                )
                    ? ErrorLevel.ERROR
                    : ErrorLevel.WARNING;
            }
        });

        return { ...errorNodes, ...shareErrors };
    }
);

export const selectProjectNodeProblemsForPropEntityId = createSelector(
    selectAllProjectNodesWithProblems,
    selectPropEntityId,
    (problems, entityId) => problems[entityId]
);

export const selectCanDeployOrInlineDraftModelForPropEntityId = createSelector(
    selectProjectNodeProblemsForPropEntityId,
    (problems) =>
        problems &&
        problems.errorLevel === ErrorLevel.WARNING &&
        !problems.errors.some(
            (error) => error.type === HAS_DRAFT_MODEL_UPSTREAM || error.type === MODEL_NOT_CONNECTED
        )
);

export const selectProjectNodeProblemsListForPropEntityId = createSelector(
    selectAllProjectNodesWithProblems,
    selectPropEntityId,
    (problems, entityId) => problems[entityId]?.errors
);

export const selectProjectNodesListWithProblems = createSelector(
    selectAllProjectNodesWithProblems,
    (allProjectProblems) => sortBy(['name'], Object.values(allProjectProblems))
);

export const selectProblemsCount = createSelector(selectProjectNodesListWithProblems, (problems) =>
    isEmpty(problems)
        ? { [ErrorLevel.ERROR]: 0, [ErrorLevel.WARNING]: 0 }
        : problems.reduce(
              (acc, node) => {
                  node.errors.forEach(({ level }) => {
                      acc[level] = (acc[level] || 0) + 1;
                  });
                  return acc;
              },
              { [ErrorLevel.ERROR]: 0, [ErrorLevel.WARNING]: 0 }
          )
);

export const selectProjectErrorNodeUids = createSelector(
    selectProjectNodesListWithProblems,
    (nodes) => nodes.filter((node) => node.errorLevel === ErrorLevel.ERROR).map(({ uid }) => uid)
);

export const selectHasErrorsForPropGroupId = createSelector(
    selectProjectNodesListWithProblems,
    selectGroups,
    selectPropEntityId,
    (errorNodes, groups, entityId) => {
        if (isEmpty(errorNodes)) {
            return false;
        }

        const group = groups.find((group) => group.entityId === entityId);
        if (isEmpty(group)) {
            return false;
        }
        const children = group.children;
        const hasGroupError = errorNodes.some(
            (node) => children.includes(node.uid) && node.errorLevel === ErrorLevel.ERROR
        );
        const hasGroupWarning = errorNodes.some(
            (node) => children.includes(node.uid) && node.errorLevel === ErrorLevel.WARNING
        );
        return { hasGroupError, hasGroupWarning };
    }
);

const selectEdgeTargetIdProp = (_, props) => props.targetId;
const parseNodeId = (uuid) => uuid?.split('.').at(-1);

const selectTargetNodes = createSelector(
    [selectDependencyGraph, selectEdgeTargetIdProp, selectFlowNodesById],
    (graph, targetId, nodesById) => {
        const targetIds = castArray(targetId).map(parseNodeId);
        const downstreamNodes = pipe(
            map((id) => graph.getDownstreamNodes(id)),
            flatten,
            concat(targetIds),
            uniq
        )(targetIds);

        return graph.nodes
            .filter((nodeId) => !downstreamNodes.includes(nodeId))
            .map((nodeId) => nodesById[nodeId])
            .filter(Boolean);
    }
);

function shouldBeDisabled(node) {
    return (
        !isEmpty(node.error) ||
        node.isDeployed ||
        node.type === ShareType.EMAIL ||
        node.type === ShareType.GOOGLE_SHEETS
    );
}

export const selectNonCyclicComboBoxOptions = createDeepEqualitySelector(
    [selectTargetNodes],
    (targetNodes) =>
        targetNodes.map((node) => ({
            uid: node.uid,
            name: node.name,
            error: !isEmpty(node.error),
            disabled: shouldBeDisabled(node)
        }))
);

export const selectNonCyclicSelectBoxOptions = createDeepEqualitySelector(
    [selectTargetNodes],
    (targetNodes) =>
        targetNodes
            .filter(({ type }) => type !== DRAFT_MODEL_DTO_TYPE)
            .map((node) => ({
                value: node.entityId,
                label: node.name,
                disabled: shouldBeDisabled(node)
            }))
);

export const selectDirectDescendantsForPropNodeId = createSelector(
    selectDependencyGraph,
    selectNodeIdProp,
    (graph, nodeId) => graph._getTargets(nodeId)
);
